import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plane, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useConfig } from '@/contexts/SystemConfigContext';
import { useSelfRemove } from '@/hooks/useFunJumper';
import WaitlistPositionIndicator from './WaitlistPositionIndicator';
import { toast } from 'sonner';

const MyStatusPanel = ({ statusData = [], isLoading }) => {
  const { t } = useTranslation();
  const { formatTime } = useConfig();
  const selfRemove = useSelfRemove();

  const handleRemove = (loadId) => {
    selfRemove.mutate(loadId, {
      onSuccess: (result) => {
        const removedFrom = result.data?.removedFrom;
        if (removedFrom === 'waitlist') {
          toast.success(t('funJumper.myStatus.leftWaitlist'));
        } else {
          toast.success(t('funJumper.myStatus.removed'));
        }
      },
      onError: (error) => {
        const code = error.response?.data?.code;
        const message = error.response?.data?.message;
        if (code === 'LOAD_CALLED') {
          toast.error(t('funJumper.errors.LOAD_CALLED'));
        } else {
          toast.error(message || t('funJumper.errors.generic'));
        }
      },
    });
  };

  if (isLoading) {
    return (
      <Card className="p-4 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  if (statusData.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-sm text-muted-foreground text-center">
          {t('funJumper.myStatus.empty')}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{t('funJumper.myStatus.title')}</h3>
      {statusData.map((entry) => {
        const isManifested = entry.me?.kind === 'manifested';
        const isWaitlisted = entry.me?.kind === 'waitlisted';

        return (
          <Card key={entry.loadId} className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plane className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {t('funJumper.myStatus.loadLabel', { number: entry.loadNumber })}
                </span>
              </div>
              <span className="text-sm">{formatTime(entry.scheduledAt)}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {entry.aircraft?.name}
                {isManifested && (
                  <span className="ml-2 text-emerald-600 font-medium">
                    {t('funJumper.myStatus.manifested')}
                  </span>
                )}
              </div>

              {isWaitlisted && (
                <WaitlistPositionIndicator
                  position={entry.me.position}
                  count={entry.me.waitlistCount}
                />
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => handleRemove(entry.loadId)}
              disabled={selfRemove.isPending}
            >
              {isWaitlisted
                ? t('funJumper.myStatus.leaveWaitlist')
                : t('funJumper.myStatus.removeButton')}
            </Button>
          </Card>
        );
      })}
    </div>
  );
};

export default MyStatusPanel;
